/**
 * Store-link attribution.
 *
 * Every marketing surface that links to the App Store or Play Store builds its
 * href here so installs can be attributed back to the site:
 *   - Apple: campaign params (`ct=stasht-web-<surface>&mt=8`, plus `pt=` once
 *     the provider token below is filled in) surface in App Store Connect →
 *     Analytics → Campaigns.
 *   - Google: a URL-encoded `referrer` param carries UTM values into Play
 *     Console acquisition reports (and the on-device Install Referrer API).
 *
 * Base URLs live in src/constants/links.ts — this module only decorates them.
 *
 * Only Stasht's own listings (identified by the app ids in those base URLs)
 * are attributed. Store links to other developers' apps — e.g. in blog
 * content — pass through untouched and untracked.
 */
import { APP_STORE_LINKS, EXTENSION_LINKS } from "@/constants/links";
import { signupAcquisition } from "./signupAcquisition";
import { storeLinkCampaign } from "./storeLinkCampaign";

/**
 * Where on the site a store link lives. Becomes the Apple campaign token
 * (`ct=stasht-web-<surface>`) and the Play referrer `utm_campaign`, and is
 * sent as the `surface` param on CTA click events.
 */
export type StoreLinkSurface =
  | "homepage"
  | "header"
  | "blog"
  | "blog-inline"
  | "landing"
  | "compare"
  | "invite"
  | "mobile-app";

export type StoreLinkDestination = "app_store" | "play_store";

/**
 * Apple provider token (`pt`) for App Store campaign links.
 *
 * TODO(Kieran/Andrew): fill in from App Store Connect → Users and Access →
 * the provider/content-provider id (shown alongside the shared secret page).
 * While empty, `pt` is omitted from links: `ct`-only links still populate the
 * ASC Campaigns report partially, but full install attribution needs `pt`.
 */
export const APPLE_PROVIDER_TOKEN = "128197713";

const STORE_DESTINATION_BY_HOSTNAME: Record<string, StoreLinkDestination> = {
  "apps.apple.com": "app_store",
  "play.google.com": "play_store",
};

/** Listing id within a store URL: Apple's trailing `/id<digits>`, Play's `?id=` package name. */
const LISTING_ID_BY_DESTINATION: Record<StoreLinkDestination, (url: URL) => string | null> = {
  app_store: url => url.pathname.match(/\/id(\d+)$/)?.[1] ?? null,
  play_store: url => url.searchParams.get("id"),
};

const listingIdForUrl = (destination: StoreLinkDestination, href: string): string | null =>
  LISTING_ID_BY_DESTINATION[destination](new URL(href));

/**
 * Stasht's own listing ids, derived from the canonical base URLs so a new
 * listing added to links.ts is scoped in automatically (currently the iOS
 * app + Safari extension on Apple, the app package on Play).
 */
const STASHT_LISTING_IDS_BY_DESTINATION: Record<StoreLinkDestination, Set<string>> = {
  app_store: new Set(
    [APP_STORE_LINKS.IOS, EXTENSION_LINKS.SAFARI_EXTENSION]
      .map(href => listingIdForUrl("app_store", href))
      .filter((id): id is string => id !== null),
  ),
  play_store: new Set(
    [APP_STORE_LINKS.ANDROID]
      .map(href => listingIdForUrl("play_store", href))
      .filter((id): id is string => id !== null),
  ),
};

const ATTRIBUTE_BY_DESTINATION: Record<StoreLinkDestination, (url: URL, campaign: ReturnType<typeof storeLinkCampaign>) => void> = {
  app_store: (url, campaign) => {
    if (APPLE_PROVIDER_TOKEN) url.searchParams.set("pt", APPLE_PROVIDER_TOKEN);
    url.searchParams.set("ct", campaign.apple);
    // mt=8 = iOS app. Preserve an existing value — the Safari extension
    // listing links with mt=12 (Mac software) and must stay that way.
    if (!url.searchParams.has("mt")) url.searchParams.set("mt", "8");
  },
  play_store: (url, campaign) => {
    // URLSearchParams percent-encodes the nested `=`/`&`, which is exactly
    // the encoding the Play install-referrer format requires.
    url.searchParams.set("referrer", campaign.play);
  },
};

/**
 * Which store one of Stasht's own listings' URL points at. Null for non-store
 * links AND for other developers' listings — those must not carry Stasht
 * campaign params or fire Stasht CTA events.
 */
export const storeDestinationForUrl = (href: string): StoreLinkDestination | null => {
  try {
    const url = new URL(href);
    const destination = STORE_DESTINATION_BY_HOSTNAME[url.hostname] ?? null;
    if (!destination) return null;

    const listingId = LISTING_ID_BY_DESTINATION[destination](url);
    if (listingId === null || !STASHT_LISTING_IDS_BY_DESTINATION[destination].has(listingId)) return null;

    return destination;
  } catch {
    return null;
  }
};

/**
 * Returns `href` with campaign attribution for `surface` when it points at a
 * store; any other URL passes through untouched. Idempotent — re-applying
 * overwrites params rather than duplicating them.
 */
export const attributeStoreUrl = (href: string, surface: StoreLinkSurface): string => {
  const destination = storeDestinationForUrl(href);
  if (!destination) return href;

  const url = new URL(href);
  const campaign = storeLinkCampaign(surface, signupAcquisition.get(), typeof window === "undefined" ? undefined : window.location.href);
  ATTRIBUTE_BY_DESTINATION[destination](url, campaign);
  return url.toString();
};

/** App Store download link attributed to `surface`. */
export const appStoreUrl = (surface: StoreLinkSurface): string => attributeStoreUrl(APP_STORE_LINKS.IOS, surface);

/** Play Store download link attributed to `surface`. */
export const playStoreUrl = (surface: StoreLinkSurface): string => attributeStoreUrl(APP_STORE_LINKS.ANDROID, surface);
